import type { ChallengeSuccessTokens } from './type'

import { L_STORAGE_KEYS } from '@src/consts'
import type { ChallengeScope } from '@src/consts/challenge-scope'

import { lStorage } from '../l-storage/l-storage'

function isChallengeSuccessTokens(
  value: unknown
): value is ChallengeSuccessTokens {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// lStorage handles the SSR (window undefined) guard and key restriction, so the
// helpers below only deal with JSON (de)serialization of the single tokens key.
function readTokens(): ChallengeSuccessTokens {
  const raw = lStorage.getItem(L_STORAGE_KEYS.CHALLENGE_SUCCESS_TOKENS)
  if (raw === null || raw === '') {
    return {}
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (isChallengeSuccessTokens(parsed)) {
      return parsed
    }
    return {}
  } catch {
    return {}
  }
}

function writeTokens(tokens: ChallengeSuccessTokens): void {
  lStorage.setItem({
    key: L_STORAGE_KEYS.CHALLENGE_SUCCESS_TOKENS,
    value: tokens,
  })
}

export const challengeToken = {
  get(scope: ChallengeScope): string | null {
    const value = readTokens()[scope]
    // Treat empty string the same as absent — consistent with has() which
    // requires length > 0.
    return value !== undefined && value.length > 0 ? value : null
  },

  set(scope: ChallengeScope, token: string): void {
    const tokens = readTokens()
    writeTokens({ ...tokens, [scope]: token })
  },

  remove(scope: ChallengeScope): void {
    const { [scope]: _removed, ...rest } = readTokens()
    writeTokens(rest)
  },

  has(scope: ChallengeScope): boolean {
    const value = readTokens()[scope]
    return value !== undefined && value.length > 0
  },
}
